import type { IsoCamera } from './IsoCamera';

/**
 * Handles mouse/touch input for camera panning and zooming.
 * Attaches to a canvas element.
 */
export class InputHandler {
  private camera: IsoCamera;
  private canvas: HTMLCanvasElement;
  private isDragging: boolean = false;
  private lastPointerX: number = 0;
  private lastPointerY: number = 0;

  // Pinch state
  private lastPinchDist: number = 0;

  // Callbacks for tile clicks
  private onTileClick?: (screenX: number, screenY: number) => void;

  // Bound event handlers (for cleanup)
  private boundPointerDown: (e: PointerEvent) => void;
  private boundPointerMove: (e: PointerEvent) => void;
  private boundPointerUp: (e: PointerEvent) => void;
  private boundWheel: (e: WheelEvent) => void;
  private boundTouchStart: (e: TouchEvent) => void;
  private boundTouchMove: (e: TouchEvent) => void;
  private boundTouchEnd: (e: TouchEvent) => void;

  constructor(camera: IsoCamera, canvas: HTMLCanvasElement) {
    this.camera = camera;
    this.canvas = canvas;

    this.boundPointerDown = this.onPointerDown.bind(this);
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerUp = this.onPointerUp.bind(this);
    this.boundWheel = this.onWheel.bind(this);
    this.boundTouchStart = this.onTouchStart.bind(this);
    this.boundTouchMove = this.onTouchMove.bind(this);
    this.boundTouchEnd = this.onTouchEnd.bind(this);

    this.attach();
  }

  setOnTileClick(callback: (screenX: number, screenY: number) => void): void {
    this.onTileClick = callback;
  }

  private attach(): void {
    this.canvas.addEventListener('pointerdown', this.boundPointerDown);
    this.canvas.addEventListener('pointermove', this.boundPointerMove);
    this.canvas.addEventListener('pointerup', this.boundPointerUp);
    this.canvas.addEventListener('pointerleave', this.boundPointerUp);
    this.canvas.addEventListener('wheel', this.boundWheel, { passive: false });
    this.canvas.addEventListener('touchstart', this.boundTouchStart, { passive: false });
    this.canvas.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    this.canvas.addEventListener('touchend', this.boundTouchEnd);
  }

  destroy(): void {
    this.canvas.removeEventListener('pointerdown', this.boundPointerDown);
    this.canvas.removeEventListener('pointermove', this.boundPointerMove);
    this.canvas.removeEventListener('pointerup', this.boundPointerUp);
    this.canvas.removeEventListener('pointerleave', this.boundPointerUp);
    this.canvas.removeEventListener('wheel', this.boundWheel);
    this.canvas.removeEventListener('touchstart', this.boundTouchStart);
    this.canvas.removeEventListener('touchmove', this.boundTouchMove);
    this.canvas.removeEventListener('touchend', this.boundTouchEnd);
  }

  private onPointerDown(e: PointerEvent): void {
    if (e.pointerType === 'touch') return; // Handled by touch events
    this.isDragging = true;
    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;
  }

  private onPointerMove(e: PointerEvent): void {
    if (e.pointerType === 'touch') return;
    if (!this.isDragging) return;

    const dx = e.clientX - this.lastPointerX;
    const dy = e.clientY - this.lastPointerY;
    this.camera.pan(-dx, -dy);
    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;
  }

  private onPointerUp(e: PointerEvent): void {
    if (e.pointerType === 'touch') return;

    // If barely moved, treat as a click
    if (this.isDragging) {
      const dx = Math.abs(e.clientX - this.lastPointerX);
      const dy = Math.abs(e.clientY - this.lastPointerY);
      if (dx < 3 && dy < 3) {
        this.onTileClick?.(e.clientX, e.clientY);
      }
    }

    this.isDragging = false;
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const rect = this.canvas.getBoundingClientRect();
    this.camera.zoomBy(zoomFactor, e.clientX - rect.left, e.clientY - rect.top);
  }

  private onTouchStart(e: TouchEvent): void {
    if (e.touches.length === 1) {
      this.isDragging = true;
      this.lastPointerX = e.touches[0]!.clientX;
      this.lastPointerY = e.touches[0]!.clientY;
    } else if (e.touches.length === 2) {
      e.preventDefault();
      this.isDragging = false;
      this.lastPinchDist = this.pinchDistance(e.touches);
    }
  }

  private onTouchMove(e: TouchEvent): void {
    if (e.touches.length === 1 && this.isDragging) {
      const dx = e.touches[0]!.clientX - this.lastPointerX;
      const dy = e.touches[0]!.clientY - this.lastPointerY;
      this.camera.pan(-dx, -dy);
      this.lastPointerX = e.touches[0]!.clientX;
      this.lastPointerY = e.touches[0]!.clientY;
    } else if (e.touches.length === 2) {
      e.preventDefault();
      const dist = this.pinchDistance(e.touches);
      if (this.lastPinchDist > 0) {
        const factor = dist / this.lastPinchDist;
        const rect = this.canvas.getBoundingClientRect();
        const cx = (e.touches[0]!.clientX + e.touches[1]!.clientX) / 2 - rect.left;
        const cy = (e.touches[0]!.clientY + e.touches[1]!.clientY) / 2 - rect.top;
        this.camera.zoomBy(factor, cx, cy);
      }
      this.lastPinchDist = dist;
    }
  }

  private onTouchEnd(e: TouchEvent): void {
    if (e.touches.length === 0) {
      this.isDragging = false;
      this.lastPinchDist = 0;
    }
  }

  private pinchDistance(touches: TouchList): number {
    const dx = touches[0]!.clientX - touches[1]!.clientX;
    const dy = touches[0]!.clientY - touches[1]!.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
